/**
 * The "digital" appearance's responsive font-sizing math — the same
 * vw-based `clamp()` values `apps/desktop/src/components/extended-
 * display/extended-display-view.tsx`'s real (non-preview) window uses
 * for its countdown/clock digits, factored out here so `ThemeLayerRenderer`
 * reproduces identical digit sizing instead of a second, independently-
 * tuned copy drifting out of sync over time. `ExtendedDisplayView`'s own
 * `preview` branch (container-query `cqw`/`cqh` units, for the small
 * Dashboard thumbnail) has no equivalent here — every consumer of this
 * package renders in a real fullscreen window or browser tab.
 */
/**
 * Tuned for a short "MM:SS" string (5 characters) — a longer one (deep
 * overtime, or an hours-and-tens-of-minutes duration with a leading "-")
 * is scaled down together so the whole string stays inside the frame
 * regardless of digit count or sign. `Math.min(1, ...)` never scales a
 * 5-character-or-shorter string up past its original size.
 */
export function digitalCountdownFontSize(digitLength) {
    const scale = Math.min(1, 5 / Math.max(digitLength, 1));
    return `clamp(${9 * scale}rem, ${26 * scale}vw, ${31 * scale}rem)`;
}
/**
 * The same "shrink for a longer string" idea as `digitalCountdownFontSize`,
 * generalized to work with *any* author-specified base size (not just the
 * "digital" appearance's own baked-in preset) — for a theme whose `timer`
 * layer sets its own `fontSize` (e.g. a custom, non-"digital" theme) and
 * still needs to avoid overflowing once the countdown goes into deep
 * overtime or the duration has hours. CSS `calc()`+`min()` does the scaling
 * at render time, so this works regardless of what unit `baseFontSize` is
 * in (`vh`, `vw`, `rem`, ...) without this function needing to parse it.
 */
export function scaledTimerFontSize(baseFontSize, digitLength) {
    const scale = Math.min(1, 5 / Math.max(digitLength, 1));
    if (scale >= 1)
        return baseFontSize;
    return `calc(${baseFontSize} * ${scale})`;
}
/** The smaller corner clock readout (e.g. an "ACTUAL TIME" panel) — `is12h` scales the whole range down, since a 12-hour string appends " AM"/" PM" (~40% wider) at the same font size. */
export function digitalClockFontSize(is12h) {
    const scale = is12h ? 0.85 : 1;
    return `clamp(${1.75 * scale}rem, ${4 * scale}vw, ${4.25 * scale}rem)`;
}
/** Static clamps for the remaining "digital" layer types — `ExtendedDisplayView`'s own values for its header labels, next-item title, current-item title, and speaker line; none of these vary by content length or clock format the way the countdown/clock ones do. */
export const DIGITAL_LABEL_FONT_SIZE = "clamp(0.75rem, 1.7vw, 1.75rem)";
export const DIGITAL_NEXT_ITEM_FONT_SIZE = "clamp(1.75rem, 4vw, 4.25rem)";
export const DIGITAL_TITLE_FONT_SIZE = "clamp(2rem, 5vw, 5.25rem)";
export const DIGITAL_SPEAKER_FONT_SIZE = "clamp(0.9rem, 1.8vw, 1.75rem)";
export const DIGITAL_MESSAGE_FONT_SIZE = "clamp(1.25rem, 2.5vw, 2.5rem)";
//# sourceMappingURL=digital-font-sizing.js.map